import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';

import Card from '@/components/Card';
import Page from '@/components/Page';
import LifecycleJourneysButton from '@/routes/Agency/Lifecycle/LifecycleJourneysButton';
import Paginator from '@/components/Paginator/Paginator';
import Stack from '@/components/Stack';
import styled from '@/styles';
import {
  ActivationFunnelData,
  Pagination,
  Skill,
  TierBoundaryLite,
  WorkerListItem,
  WorkersListParams,
  WorkersV2API,
} from '@/services/workersV2API';

import SectionErrorBoundary from '../WorkerV2/components/SectionErrorBoundary';
import WorkerCard from '../WorkersV2/components/WorkerCard';
import { normalizeWorkerSearch } from '../WorkersV2/helpers';
import FunnelSummary from './components/FunnelSummary';
import FilterRow, { GravyWorkersFilters } from './components/FilterRow';
import OnboardingTable, { headersFor } from './components/OnboardingTable';
import WorkerPanel from './components/WorkerPanel';
import WorkersTable, {
  NO_MATCH_TEXT,
  SORT_COLUMNS as ALL_TAB_SORTS,
  SortColumn,
} from './components/WorkersTable';
import { RADIUS, TAB_OPTIONS } from './mappings';
import { ColumnTab, RANGE_OPTIONS, RangeDays } from './onboardingMappings';
import {
  DEFAULT_SORT,
  GravyWorkersUrlState,
  parseUrlState,
  serializeUrlState,
  Tab,
} from './urlState';

// The page column is `Stack vertical` (align-items: flex-start), so without an
// explicit width this row shrinks to its content and `space-between` spreads
// nothing (GRAVY-10815).
const Toolbar = styled('div', {
  display: 'flex',
  width: '100%',
  gap: '12px',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
});
// The input keeps its 36px left padding for the magnifier drawn over it.
// A global style makes inputs `position: relative`, and the input comes after
// the icon, so without the z-index the input's white background hides it.
const SearchWrap = styled('div', {
  position: 'relative',
  width: '300px',
  maxWidth: '100%',
  '& > svg': {
    position: 'absolute',
    zIndex: 1,
    left: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#6B6B6B',
    fontSize: '20px',
    pointerEvents: 'none',
  },
});
const Search = styled('input', {
  height: '40px',
  width: '100%',
  boxSizing: 'border-box',
  padding: '0 12px 0 36px',
  border: '1px solid #DADADA',
  borderRadius: '8px',
  fontSize: '14px',
});
const Tabs = styled('div', {
  display: 'inline-flex',
  border: '1px solid #DADADA',
  borderRadius: '8px',
  padding: '4px',
  gap: '4px',
  '@media (max-width: 768px)': { flexWrap: 'wrap' },
});
// Right-aligned on the search row per the design, and still right-aligned when
// the row wraps at narrow desktop widths. Below 768px the group takes the full
// width and wraps, so the four tabs plus Show cannot push past the viewport.
const ToolbarActions = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  width: 'auto',
  marginLeft: 'auto',
  '@media (max-width: 768px)': {
    width: '100%',
    marginLeft: 0,
    flexWrap: 'wrap',
  },
});
const MobileEmpty = styled('div', {
  padding: '48px 16px',
  textAlign: 'center',
  color: '#5A5A5A',
});
const TabBtn = styled('button', {
  appearance: 'none',
  border: 'none',
  background: 'transparent',
  padding: '6px 12px',
  borderRadius: '6px',
  fontSize: '14px',
  cursor: 'pointer',
  variants: {
    selected: {
      true: { backgroundColor: '#1E8E3E', color: '#FFFFFF', fontWeight: 600 },
    },
  },
});
const ShowBtn = styled('button', {
  appearance: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  height: '40px',
  padding: '0 14px',
  border: '1px solid #DADADA',
  borderRadius: '8px',
  background: '#FFFFFF',
  cursor: 'pointer',
  fontSize: '14px',
  variants: { open: { true: { borderColor: '#1E8E3E', color: '#1E8E3E' } } },
});
const RangeGroup = styled('div', {
  display: 'inline-flex',
  border: '1px solid #DADADA',
  borderRadius: '8px',
  padding: '4px',
  gap: '4px',
  backgroundColor: '#FFFFFF',
});
const FilterBar = styled('div', {
  display: 'flex',
  width: '100%',
  gap: '12px',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
});
const Split = styled('div', {
  display: 'flex',
  width: '100%',
  gap: '16px',
  alignItems: 'flex-start',
});
const ListColumn = styled('div', {
  flex: '1 1 auto',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});
const MobileList = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '12px',
});
const RetryBtn = styled('button', {
  marginLeft: '8px',
  border: '1px solid #DADADA',
  borderRadius: '6px',
  background: '#FFFFFF',
  padding: '2px 10px',
  cursor: 'pointer',
});

const PanelError = styled('aside', {
  flex: '0 0 400px',
  maxWidth: '400px',
  boxSizing: 'border-box',
  padding: '16px',
  border: '1px solid #DADADA',
  borderRadius: '12px',
  backgroundColor: '#FFFFFF',
  color: '#6B6B6B',
  fontSize: '14px',
});

// The panel's own Close button and Escape listener went down with it, so
// the fallback keeps a way to close it (BH-33).
const PanelFallback = ({ onClose }: { onClose: () => void }) => (
  <PanelError role="alert" data-testid="workerPanelError">
    Couldn&apos;t load this worker&apos;s details. Try refreshing the page.
    <RetryBtn type="button" onClick={onClose}>
      Close
    </RetryBtn>
  </PanelError>
);

const isColumnTab = (tab: Tab): tab is ColumnTab => tab !== 'all';

// A sort only carries into a tab that has its column; otherwise the list
// would be ordered by something the tab neither shows nor marks (review D-08).
const sortsOn = (tab: Tab): SortColumn[] =>
  isColumnTab(tab)
    ? headersFor(tab, false).flatMap((h) => (h.sortKey ? [h.sortKey] : []))
    : ALL_TAB_SORTS;

const NO_FILTERS: GravyWorkersFilters = { radius: RADIUS.default };
const hasFilters = (f: GravyWorkersFilters) =>
  Object.entries(f).some(([key, value]) =>
    key === 'radius'
      ? value !== RADIUS.default
      : Array.isArray(value)
        ? value.length > 0
        : Boolean(value),
  );

// The All tab is the GRAVY-10436 page (its own advanced filters); the
// Onboarding / BG check / I-9 tabs share one filter row and the sign-up range.
// Both groups have Tax type, Days stuck and Zip filters, under one URL key
// each, so switching group clears the filters (switchTab) and an All tab link
// with filters opens its panel: a value never narrows a list out of sight
// (review D-02).
export const listParams = (
  state: GravyWorkersUrlState,
  search: string,
): WorkersListParams => {
  const { filters: f } = state;
  const p: WorkersListParams = {
    page: state.page,
    per_page: state.perPage,
    sort_by: state.sort.by,
    sort_direction: state.sort.direction,
  };
  const q = normalizeWorkerSearch(search);
  if (q) p.search = q;
  if (f.tax_type) p.tax_type = f.tax_type;
  if (f.progress) p.progress = f.progress;
  if (f.zip && f.zip.length === 5) {
    p.zip = f.zip;
    p.radius = f.radius || RADIUS.default;
  }
  if (isColumnTab(state.tab)) {
    p.signed_up_within_days = state.range;
    if (f.current_status) p.current_status = f.current_status;
    // The KPI cards count active sign-ups only (ActivationFunnelService
    // cohort), so the list does too, or the cards and the row count disagree.
    // Picking Status = Disabled is the one way to list deactivated workers.
    p.active = f.current_status === 'disabled' ? 'all' : true;
    return p;
  }
  if (f.skill_ids?.length) p.skill_ids = f.skill_ids;
  if (f.dependability_v2_tier)
    p.dependability_v2_tier = [f.dependability_v2_tier];
  if (f.last_active) p.last_active = f.last_active;
  // PD-6: the design lists Disabled workers by default; the legacy API hides them.
  p.active = f.active ? f.active === 'true' : 'all';
  if (f.accountability_status)
    p.accountability_status = [f.accountability_status];
  return p;
};

const GravyWorkers = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [searchParams, setSearchParams] = useSearchParams();
  // A hand-edited or old link can pair a tab with a sort it has no column
  // for (?tab=background_check&sort=onboarding_step); it gets the default
  // sort, as switchTab gives (review D-08).
  const state = useMemo(() => {
    const s = parseUrlState(searchParams);
    return s.sort.by === DEFAULT_SORT.by || sortsOn(s.tab).includes(s.sort.by)
      ? s
      : { ...s, sort: DEFAULT_SORT };
  }, [searchParams]);
  const { tab, range, filters, sort, perPage, workerId } = state;

  const [funnel, setFunnel] = useState<ActivationFunnelData | null>(null);
  const [funnelLoading, setFunnelLoading] = useState(true);
  const [funnelError, setFunnelError] = useState(false);
  const [funnelReload, setFunnelReload] = useState(0);
  const [workers, setWorkers] = useState<WorkerListItem[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [boundaries, setBoundaries] = useState<TierBoundaryLite[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [searchInput, setSearchInput] = useState(state.search);
  const [showFilters, setShowFilters] = useState(
    () => state.tab === 'all' && hasFilters(state.filters),
  );
  // The ?worker= row when it is not on the loaded page (see below).
  const [pinned, setPinned] = useState<WorkerListItem | null>(null);
  const [pinnedFor, setPinnedFor] = useState<string | null>(null);
  const [lookupFailed, setLookupFailed] = useState(false);
  const [lookupReload, setLookupReload] = useState(0);

  const requestSeq = useRef(0);

  // Every change goes through the URL (WL-9); a change other than paging
  // returns to page 1. The patch applies to the URL as it is when it lands,
  // not as it was when the caller rendered: the search box applies 500 ms
  // late, and spreading its stale copy would undo a tab or filter clicked
  // meanwhile (review D-01). A ref, because react-router's functional
  // setSearchParams reads the params its own closure captured.
  const latestParams = useRef(searchParams);
  latestParams.current = searchParams;
  const update = useCallback(
    (patch: Partial<GravyWorkersUrlState>, keepPage = false) => {
      setSearchParams(
        serializeUrlState({
          ...parseUrlState(latestParams.current),
          ...(keepPage ? {} : { page: 1 }),
          ...patch,
        }),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    WorkersV2API.getSkills()
      .then((r) => r.success && setSkills(r.data))
      .catch((err) => console.error('[GravyWorkers] Error:', err));
  }, []);

  // The KPI cards count the same sign-up window the list shows on the
  // Onboarding tabs; the All tab keeps the 365-day funnel.
  const funnelDays = isColumnTab(tab) ? range : undefined;
  useEffect(() => {
    let cancelled = false;
    setFunnelLoading(true);
    setFunnelError(false);
    WorkersV2API.getActivationFunnel(funnelDays)
      .then((r) => {
        if (cancelled) return;
        if (r.success) setFunnel(r.data);
        else setFunnelError(true);
      })
      .catch((err) => {
        console.error('[GravyWorkers] Error:', err);
        if (!cancelled) setFunnelError(true);
      })
      .finally(() => !cancelled && setFunnelLoading(false));
    return () => {
      cancelled = true;
    };
  }, [funnelDays, funnelReload]);

  // The URL can change without a remount (a sidebar link to /gravyworkers,
  // back/forward): the box follows it, or it shows a search the list no
  // longer applies. Typing never trips this: the URL only ever receives the
  // box's own value, after the debounce below.
  useEffect(() => {
    setSearchInput(state.search);
  }, [state.search]);

  useEffect(() => {
    if (searchInput === state.search) return undefined;
    const t = setTimeout(() => update({ search: searchInput }), 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Keyed on the request's value, not the URL: opening or closing a worker
  // changes `worker=` only and must not reload the list (review D-09).
  const paramsKey = JSON.stringify(listParams(state, state.search));
  const params = useMemo(
    () => JSON.parse(paramsKey) as WorkersListParams,
    [paramsKey],
  );
  // What the request narrows by, not the URL: a 4-digit zip is not sent, so
  // it does not count. The sign-up range is the tab's window, not a filter.
  const filtered = Boolean(
    params.search ||
      params.zip ||
      params.current_status ||
      params.progress ||
      params.tax_type,
  );

  // The rows on screen belong to the previous filters, range or page; keeping
  // them under the new selection would misreport it (review D3-05).
  const failList = () => {
    setError('Failed to fetch workers');
    setWorkers([]);
    setPagination(null);
    setLastUpdated(null);
  };

  const fetchWorkers = useCallback(async () => {
    const seq = ++requestSeq.current;
    setListLoading(true);
    try {
      setError(null);
      const res = await WorkersV2API.getWorkers(params);
      if (seq !== requestSeq.current) return;
      if (res.success) {
        // A shared or stale link past the last page gets no rows but a total:
        // go to the last page rather than claim "Nobody is stuck here"
        // (review R3D-08).
        const last = res.pagination?.total_pages ?? 0;
        if (res.data.length === 0 && last > 0 && (params.page ?? 1) > last) {
          // Retire this request so `finally` leaves the loading state on
          // for the next one, with no flash of the empty result.
          requestSeq.current += 1;
          update({ page: last }, true);
          return;
        }
        setWorkers(res.data);
        setPagination(res.pagination);
        setBoundaries(res.meta?.tier_boundaries ?? []);
        setLastUpdated(new Date());
      } else {
        failList();
      }
    } catch (err) {
      if (seq !== requestSeq.current) return;
      console.error('[GravyWorkers] Error:', err);
      failList();
    } finally {
      if (seq === requestSeq.current) setListLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchWorkers();
  }, [fetchWorkers]);

  const switchTab = (next: Tab) =>
    update({
      tab: next,
      workerId: null,
      ...(isColumnTab(next) === isColumnTab(tab)
        ? {}
        : { filters: NO_FILTERS }),
      ...(sort.by === DEFAULT_SORT.by || sortsOn(next).includes(sort.by)
        ? {}
        : { sort: DEFAULT_SORT }),
    });

  const onTabKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const buttons = Array.from(
      e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'),
    );
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (at < 0) return;
    const last = buttons.length - 1;
    const next = {
      ArrowRight: at === last ? 0 : at + 1,
      ArrowLeft: at === 0 ? last : at - 1,
      Home: 0,
      End: last,
    }[e.key];
    if (next === undefined) return;
    e.preventDefault();
    buttons[next].focus();
  };

  // A new column starts ascending, except Stuck for: ops open it to find who
  // has waited longest, and ascending put the 0-day sign-ups first.
  const onSort = (column: SortColumn) =>
    update({
      sort: {
        by: column,
        direction:
          sort.by === column
            ? sort.direction === 'asc'
              ? 'desc'
              : 'asc'
            : column === 'stuck_for'
              ? 'desc'
              : 'asc',
      },
    });

  const skillsForRow = useMemo(
    () => skills.map((s) => ({ id: s.id, name: s.display_name_en })),
    [skills],
  );

  const activationShifts = funnel?.thresholds.activation_shifts ?? 5;
  const onPage = workers.find((w) => w.id === workerId) ?? null;
  useEffect(() => setLookupFailed(false), [workerId]);
  // A shared ?worker= link can name a worker who is not on the loaded page
  // (new sign-ups push rows down, or the link carries other filters); fetch
  // that one row so the panel still opens instead of silently not showing.
  // A row found under other filters is looked up again under the new ones. It
  // stays on screen meanwhile: clearing it would remount the panel, whose
  // heading takes focus, so each search keystroke would leave the search box.
  useEffect(() => {
    // Not after a failed load: the empty list is the failure, not a sign the
    // worker left it (review R3D-04); `selected` hides the panel meanwhile.
    if (!isColumnTab(tab) || !workerId || listLoading || error)
      return undefined;
    if (onPage || lookupFailed) return undefined;
    if (pinned?.id === workerId && pinnedFor === paramsKey) return undefined;
    let cancelled = false;
    // A failed lookup says so, with Retry: otherwise a shared link opens no
    // panel and gives no reason (review R5B-06).
    const failLookup = () => !cancelled && setLookupFailed(true);
    // With the list's own filters (graphy's ids[] narrows them, never widens
    // them): a worker on another page of this list opens, and one a filter,
    // range or search change removed closes, instead of a panel beside a
    // list that no longer has them.
    WorkersV2API.getWorkers({
      ...params,
      ids: [workerId],
      page: 1,
      per_page: 1,
    })
      .then((r) => {
        if (cancelled) return;
        if (!r.success) {
          failLookup();
          return;
        }
        // By id: a graphy without the ids[] filter answers with the first
        // worker of the whole list (review R3D-05).
        const row = r.data.find((w) => w.id === workerId);
        if (row) {
          setPinned(row);
          setPinnedFor(paramsKey);
        }
        // Nobody by that id: drop it, or every list load asks again (D3-04).
        else closePanel();
      })
      .catch((err) => {
        console.error('[GravyWorkers] Error:', err);
        failLookup();
      });
    return () => {
      cancelled = true;
    };
  }, [
    tab,
    workerId,
    listLoading,
    error,
    onPage,
    pinned,
    pinnedFor,
    paramsKey,
    lookupFailed,
    lookupReload,
    params,
  ]);
  // No panel beside a failed list, however it was opened (a row or a shared
  // link's lookup); `worker=` stays, so Retry brings it back (review R4-02).
  const selected =
    isColumnTab(tab) && !error
      ? (onPage ?? (pinned?.id === workerId && !lookupFailed ? pinned : null))
      : null;
  const openProfile = (id: number, profileTab?: string) =>
    navigate(
      profileTab ? `/workers_v2/${id}?tab=${profileTab}` : `/workers_v2/${id}`,
    );
  // BH-72: a phone has no side panel (a card tap opens the profile), so a
  // shared ?worker= link showed the list with no worker and no message. It
  // opens that worker's profile, as the tap would. `replace`, so Back leaves
  // for where the link came from instead of bouncing to the profile again.
  useEffect(() => {
    if (isMobile && workerId && isColumnTab(tab))
      navigate(`/workers_v2/${workerId}`, { replace: true });
  }, [isMobile, workerId, tab, navigate]);
  // "Rejected. View details" opens the panel at the rejected step, also for
  // the worker already open, whose Onboarding section may be collapsed: the
  // reason is the point of the link (review R5A-02). A fresh number per
  // click; any other way of opening or closing clears it, so a panel opened
  // later from a row does not jump to a rejection.
  const revealSeq = useRef(0);
  const [revealRejected, setRevealRejected] = useState(0);
  // Opening the worker who is already open does nothing (ticket edge case).
  const selectWorker = (id: number) => {
    setRevealRejected(0);
    if (id !== workerId) update({ workerId: id }, true);
  };
  const viewRejected = (id: number) => {
    selectWorker(id);
    revealSeq.current += 1;
    setRevealRejected(revealSeq.current);
  };
  const closePanel = useCallback(() => {
    setRevealRejected(0);
    update({ workerId: null }, true);
  }, [update]);

  return (
    <Page customActions={<LifecycleJourneysButton />} title="GravyWorkers">
      <Stack vertical gap={16}>
        {funnelError ? (
          <div role="alert" data-testid="funnelError">
            Could not load the summary cards.
            <RetryBtn
              type="button"
              onClick={() => setFunnelReload((n) => n + 1)}
            >
              Retry
            </RetryBtn>
          </div>
        ) : (
          <FunnelSummary
            data={funnel}
            loading={funnelLoading}
            variant={isColumnTab(tab) ? 'onboarding' : 'all'}
          />
        )}

        <Toolbar>
          <SearchWrap>
            <SearchIcon aria-hidden="true" data-testid="searchIcon" />
            <Search
              data-testid="searchInput"
              aria-label="Search gravyworker"
              placeholder="Search gravyworker"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </SearchWrap>
          <ToolbarActions>
            {/* A tablist with one Tab stop and arrow keys, linked to the
                list it switches, so a screen reader can tell which view the
                table shows (ticket AC: the selected tab is announced; review
                R5A-03). Arrows move focus only: a tab loads on Enter or
                click, so arrowing past a tab does not fetch its list. */}
            <Tabs
              role="tablist"
              aria-label="Worker views"
              onKeyDown={onTabKeyDown}
            >
              {TAB_OPTIONS.map((t) => (
                <TabBtn
                  key={t.value}
                  id={`workersTab-${t.value}`}
                  role="tab"
                  aria-selected={tab === t.value}
                  aria-controls="workersTabPanel"
                  tabIndex={tab === t.value ? 0 : -1}
                  selected={tab === t.value}
                  onClick={() => switchTab(t.value)}
                >
                  {t.label}
                </TabBtn>
              ))}
            </Tabs>
            {tab === 'all' && (
              <ShowBtn
                data-testid="filterBtn"
                open={showFilters}
                aria-expanded={showFilters}
                onClick={() => setShowFilters((v) => !v)}
              >
                <FilterListIcon sx={{ fontSize: 18 }} />
                <span data-testid="filterBtnLabel">
                  {showFilters ? 'Hide' : 'Show'}
                </span>
              </ShowBtn>
            )}
          </ToolbarActions>
        </Toolbar>

        {isColumnTab(tab) && (
          <FilterBar>
            <RangeGroup role="group" aria-label="Sign-up range">
              {RANGE_OPTIONS.map((days) => (
                <TabBtn
                  key={days}
                  type="button"
                  aria-pressed={range === days}
                  selected={range === days}
                  data-testid={`range-${days}`}
                  onClick={() => update({ range: days as RangeDays })}
                >
                  {days} days
                </TabBtn>
              ))}
            </RangeGroup>
            <FilterRow
              variant="onboarding"
              filters={filters}
              skills={skillsForRow}
              onChange={(next: GravyWorkersFilters) =>
                update({ filters: next })
              }
            />
          </FilterBar>
        )}

        {tab === 'all' && showFilters && (
          <FilterRow
            filters={filters}
            skills={skillsForRow}
            onChange={(next) => update({ filters: next })}
          />
        )}

        {error && (
          <div role="alert" data-testid="listError">
            {error}
            <RetryBtn type="button" onClick={() => fetchWorkers()}>
              Retry
            </RetryBtn>
          </div>
        )}

        {/* Not once the worker is open from the list itself, e.g. after a
            search finds them (review R6A-01). */}
        {lookupFailed && !selected && (
          <div role="alert" data-testid="workerLookupError">
            Could not load this worker.
            <RetryBtn
              type="button"
              onClick={() => {
                setLookupFailed(false);
                setLookupReload((n) => n + 1);
              }}
            >
              Retry
            </RetryBtn>
          </div>
        )}

        <Split
          id="workersTabPanel"
          role="tabpanel"
          aria-labelledby={`workersTab-${tab}`}
        >
          <ListColumn>
            <Card>
              {/* A malformed row must not blank the whole page (BH-33).
                  Keyed by the request, so another tab, filter or page gets
                  a fresh try. */}
              <SectionErrorBoundary
                key={`${tab}-${paramsKey}`}
                label="the worker list"
              >
                {isMobile && (
                  <MobileList
                    aria-busy={
                      listLoading && workers.length > 0 ? true : undefined
                    }
                    style={
                      listLoading && workers.length > 0
                        ? { opacity: 0.5 }
                        : undefined
                    }
                  >
                    {/* The tables carry these states; on a phone the cards
                      alone left a blank card while loading or when nothing
                      matched. */}
                    {workers.length === 0 && listLoading && (
                      <MobileEmpty data-testid="listLoading" aria-busy="true">
                        Loading workers…
                      </MobileEmpty>
                    )}
                    {workers.length === 0 && !listLoading && !error && (
                      <MobileEmpty data-testid="emptyState">
                        {isColumnTab(tab) && !filtered
                          ? 'Nobody is stuck here.'
                          : NO_MATCH_TEXT}
                      </MobileEmpty>
                    )}
                    {workers.map((w) => (
                      <WorkerCard
                        key={w.id}
                        worker={w}
                        onClick={() => openProfile(w.id)}
                      />
                    ))}
                  </MobileList>
                )}
                {!isMobile && tab === 'all' && (
                  <WorkersTable
                    workers={workers}
                    sort={sort}
                    onSort={onSort}
                    tierBoundaries={boundaries}
                    onWorkerClick={openProfile}
                    loading={listLoading}
                  />
                )}
                {!isMobile && isColumnTab(tab) && (
                  <OnboardingTable
                    tab={tab}
                    workers={workers}
                    sort={sort}
                    onSort={onSort}
                    selectedId={selected?.id ?? null}
                    onSelect={selectWorker}
                    onViewRejected={viewRejected}
                    onOpenProfile={openProfile}
                    activationShifts={activationShifts}
                    loading={listLoading}
                    failed={Boolean(error)}
                    filtered={filtered}
                  />
                )}
              </SectionErrorBoundary>
            </Card>
            {pagination && pagination.total > 0 && (
              <Paginator
                currentPage={pagination.page}
                handlePageChange={(p: number) =>
                  update({ page: p, workerId: null }, true)
                }
                handleNumberItemsChange={(e) =>
                  update({ perPage: parseInt(e.target.value, 10) })
                }
                itemsLength={pagination.total}
                itemsPerPage={perPage}
              />
            )}
            {lastUpdated && (
              <div
                data-testid="lastUpdated"
                style={{ fontSize: 12, color: '#5A5A5A' }}
              >
                {/* The rows above are the previous selection's until the
                    reload lands; the old time would vouch for them (R5B-05). */}
                {listLoading
                  ? 'Updating…'
                  : `Last updated ${lastUpdated.toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                    })}`}
              </div>
            )}
          </ListColumn>
          {selected && !isMobile && (
            // Its own boundary, keyed by worker: one worker's bad data
            // leaves the list usable, and opening another retries.
            <SectionErrorBoundary
              key={selected.id}
              fallback={<PanelFallback onClose={closePanel} />}
            >
              <WorkerPanel
                worker={selected}
                activationShiftsTarget={activationShifts}
                onClose={closePanel}
                onOpenProfile={openProfile}
                revealRejected={revealRejected}
              />
            </SectionErrorBoundary>
          )}
        </Split>
      </Stack>
    </Page>
  );
};

export default GravyWorkers;
